// src/components/common/Toast.tsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

export type ToastType = "success" | "error" | "warning" | "info";
export type ToastPosition = "top-right" | "bottom-right";

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  position?: ToastPosition;
  action?: ToastAction;
  onClose: (id: string) => void;
}

// Toast 圖標組件
const ToastIcon: React.FC<{ type: ToastType }> = ({ type }) => {
  const iconProps = {
    className: "w-5 h-5",
    fill: "none",
    stroke: "currentColor",
    viewBox: "0 0 24 24",
  };

  switch (type) {
    case "success":
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      );
    case "error":
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      );
    case "warning":
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      );
    case "info":
      return (
        <svg {...iconProps}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
  }
};

// 單個 Toast 組件
const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 4000,
  position = "bottom-right",
  action,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // 類型樣式配置 - 符合Orlo設計風格
  const typeStyles = {
    success: {
      container: "bg-success-50 border-success-200 shadow-sm",
      icon: "text-success-600",
      title: "text-success-800",
      message: "text-success-700",
    },
    error: {
      container: "bg-error-50 border-error-200 shadow-sm",
      icon: "text-error-600",
      title: "text-error-800",
      message: "text-error-700",
    },
    warning: {
      container: "bg-warning-50 border-warning-200 shadow-sm",
      icon: "text-warning-600",
      title: "text-warning-800",
      message: "text-warning-700",
    },
    info: {
      container: "bg-sky-50 border-sky-200 shadow-sm",
      icon: "text-sky-600",
      title: "text-sky-800",
      message: "text-sky-700",
    },
  };

  const styles = typeStyles[type];

  // 響應式位置樣式 - 行動版置中且避開導航，桌面版右下角
  const positionStyles =
    position === "top-right"
      ? "top-16 left-4 right-4 pt-safe sm:top-6 sm:left-auto sm:right-6" // 行動版：頂部置中，左右16px邊距
      : "bottom-20 left-4 right-4 pb-safe sm:bottom-6 sm:left-auto sm:right-6"; // 行動版：底部80px避開導航，桌面版24px

  // 動畫控制
  useEffect(() => {
    // 進入動畫
    const showTimer = setTimeout(() => setIsVisible(true), 50);

    // 用於追蹤巢狀timer
    let closeTimer: NodeJS.Timeout;

    // 自動關閉
    const hideTimer = setTimeout(() => {
      setIsLeaving(true);
      closeTimer = setTimeout(() => onClose(id), 300);
    }, duration);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [id, duration, onClose]);

  // 手動關閉 - 使用useRef追蹤timer避免重複關閉
  const manualCloseTimerRef = useRef<NodeJS.Timeout>();

  const handleClose = useCallback(() => {
    // 防止重複關閉
    if (isLeaving) return;

    setIsLeaving(true);

    // 清理之前的timer（如果存在）
    if (manualCloseTimerRef.current) {
      clearTimeout(manualCloseTimerRef.current);
    }

    manualCloseTimerRef.current = setTimeout(() => onClose(id), 300);
  }, [id, onClose, isLeaving]);

  // 組件卸載時清理手動關閉timer
  useEffect(() => {
    return () => {
      if (manualCloseTimerRef.current) {
        clearTimeout(manualCloseTimerRef.current);
      }
    };
  }, []);

  // 動畫類別 - 使用專為 Toast 設計的動畫
  const animationClasses = isLeaving
    ? "animate-toastSlideOut"
    : isVisible
      ? "animate-toastSlideIn"
      : "opacity-0 scale-95";

  return createPortal(
    <div
      className={`
        fixed z-toast
        ${positionStyles}
        ${animationClasses}
      `}
    >
      <div
        className={`
          flex items-start gap-3 p-4
          border rounded-lg
          max-w-md w-full mx-auto sm:w-auto sm:mx-0
          ${styles.container}
        `}
        role="alert"
        aria-live="polite"
      >
        {/* 圖標 */}
        <div
          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center ${styles.icon}`}
        >
          <ToastIcon type={type} />
        </div>

        {/* 內容 */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium text-sm ${styles.title}`}>{title}</h4>
          {message && (
            <p className={`mt-1 text-sm ${styles.message}`}>{message}</p>
          )}
        </div>

        {/* Action button（如 Undo） */}
        {action && (
          <button
            onClick={async () => {
              try {
                await action.onClick();
              } finally {
                handleClose();
              }
            }}
            className="flex-shrink-0 text-sm font-medium text-sky-600 hover:text-sky-800 transition-colors px-1"
          >
            {action.label}
          </button>
        )}

        {/* 關閉按鈕 - 符合HIG/Material Design觸控目標 */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
          aria-label="關閉通知"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default Toast;
